import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { LoadingState } from '../../ui/loading-state';
import { TERMS_ACCEPTED_VERSION, PRIVACY_POLICY_ACCEPTED_VERSION } from '../../../utils/constants';

interface TermsGuardProps {
  children: React.ReactNode;
}

const TermsGuard = ({ children }: TermsGuardProps): JSX.Element => {
  const { userData, loading: userLoading } = useAuth();
  const location = useLocation();

  if (userLoading || !userData) {
    return <LoadingState />;
  }

  // Check if user has accepted terms and privacy policy
  const hasAcceptedTerms = userData?.terms_accepted?.version === TERMS_ACCEPTED_VERSION;
  const hasAcceptedPrivacy = userData?.privacy_policy_accepted?.version === PRIVACY_POLICY_ACCEPTED_VERSION;
  const hasAcceptedBoth = hasAcceptedTerms && hasAcceptedPrivacy;

  // Allow direct access to terms and privacy policy pages
  if (location.pathname === '/terms' || location.pathname === '/privacy') {
    return <>{children}</>;
  }

  // If user has accepted terms and tries to access accept-terms page, redirect to home
  if (hasAcceptedBoth && location.pathname === '/accept-terms') {
    return <Navigate to="/" replace />;
  }

  // If user hasn't accepted terms and isn't on the accept-terms page, redirect to accept terms
  if (!hasAcceptedBoth && location.pathname !== '/accept-terms') {
    return <Navigate to={`/accept-terms?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <>{children}</>;
};

export default TermsGuard;